import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { teamNameInputSchema } from "@bezoned/shared/schemas/team.schema.js";
import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { Button, Field, Input } from "@fluentui/react-components";
import { useMemo, useState } from "react";
import type { ChangeEvent, FormEvent, ReactNode } from "react";

import { TeamEditLogoSection } from "../team-edit/tabs/team-edit-logo-section.js";

export interface TeamGeneralFormValue {
  name: string;
}

interface TeamGeneralTabProps {
  children?: ReactNode;
  externalValidationMessage?: string | null;
  initialName: string;
  onSubmitName: (value: TeamGeneralFormValue) => Promise<void> | void;
  submitLabel?: string;
  teamLogo?: {
    msTeamId: string;
    onChange: () => void;
    onError: (error: unknown) => void;
    teamQueryData: TeamOfficeGetResponse | undefined;
  };
}

const getValidationMessage = (value: string): string | null => {
  const parsed = teamNameInputSchema.safeParse(value);

  if (parsed.success) {
    return null;
  }

  const [firstIssue] = parsed.error.issues;

  if (!firstIssue) {
    return "Office name is required";
  }

  return firstIssue.message;
};

const getCurrentValidationMessage = ({
  externalValidationMessage,
  hasUserUpdatedName,
  rawValidationMessage,
}: {
  externalValidationMessage: string | null;
  hasUserUpdatedName: boolean;
  rawValidationMessage: string | null;
}): string | null => {
  if (hasUserUpdatedName && rawValidationMessage) {
    return rawValidationMessage;
  }

  if (externalValidationMessage) {
    return externalValidationMessage;
  }

  return null;
};

export const TeamGeneralTab = ({
  children,
  externalValidationMessage = null,
  initialName,
  onSubmitName,
  submitLabel = "Save",
  teamLogo,
}: TeamGeneralTabProps) => {
  const [nameInput, setNameInput] = useState(initialName);
  const [savedName, setSavedName] = useState(initialName);
  const [hasUserUpdatedName, setHasUserUpdatedName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dismissedExternalValidation, setDismissedExternalValidation] =
    useState<string | null>(null);

  const trimmedName = useMemo(() => nameInput.trim(), [nameInput]);
  const rawValidationMessage = useMemo(
    () => getValidationMessage(nameInput),
    [nameInput]
  );

  const hasNameChanged = trimmedName !== savedName;

  const handleNameChange = useStableCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHasUserUpdatedName(true);
      setDismissedExternalValidation(externalValidationMessage);
      setNameInput(event.target.value);
    }
  );

  const externalValidationState =
    externalValidationMessage === dismissedExternalValidation
      ? null
      : externalValidationMessage;
  const currentValidationMessage = getCurrentValidationMessage({
    externalValidationMessage: externalValidationState,
    hasUserUpdatedName,
    rawValidationMessage,
  });

  const validationState = currentValidationMessage ? "error" : "none";
  const saveButtonLabel = isSubmitting ? "Saving..." : submitLabel;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      isSubmitting ||
      !hasNameChanged ||
      rawValidationMessage !== null
    ) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmitName({ name: trimmedName });
      setSavedName(trimmedName);
      setNameInput(trimmedName);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit}>
        <Field
          className="w-full"
          label="Office name"
          validationMessage={currentValidationMessage}
          validationState={validationState}
        >
          <Input
            className="w-full"
            disabled={isSubmitting}
            onChange={handleNameChange}
            value={nameInput}
          />
        </Field>

        <div className="flex items-center justify-end gap-3">
          <Button
            appearance="primary"
            disabled={
              isSubmitting ||
              !hasNameChanged ||
              rawValidationMessage !== null
            }
            type="submit"
          >
            {saveButtonLabel}
          </Button>
        </div>
      </form>
      {teamLogo ? <TeamEditLogoSection {...teamLogo} /> : null}
      {children}
    </div>
  );
};
